/**
 * A stored write-only credential is bound to the destination it was entered
 * for. The backend rejects a save that changes a destination field (URL,
 * server CA, or turns TLS verification off) unless every stored credential is
 * re-entered or explicitly cleared (sent as ""). The record screen therefore
 * asks the user to confirm before such a save clears the stored credentials.
 *
 * Kept in a standalone module (no heavy config/router imports) so it can be
 * unit-tested in isolation.
 */
interface CredentialBinding {
    destinationFields: string[];
    tlsWeakeningFields: string[];
    credentialFields: string[];
}

export const credentialBindingByResource: { [resourceName: string]: CredentialBinding } = {
    bundle_repository: {
        destinationFields: ["remote_url", "server_ca"],
        tlsWeakeningFields: ["verify_ssl", "check_hostname"],
        credentialFields: ["api_key", "write_key"],
    },
    vault_config: {
        destinationFields: ["vault_url", "server_ca"],
        tlsWeakeningFields: ["verify_ssl", "check_hostname"],
        credentialFields: ["token", "password", "client_key"],
    },
    ldap_config: {
        destinationFields: ["server_url", "cacert"],
        tlsWeakeningFields: ["verify_tls", "use_starttls"],
        credentialFields: ["password", "client_key"],
    },
    devolutions_config: {
        destinationFields: ["devolutions_url", "cacert"],
        tlsWeakeningFields: ["verify_ssl"],
        credentialFields: ["app_secret"],
    },
};

const norm = (value: unknown) => (value == null ? "" : value);

export interface CredentialClearing {
    changedFields: string[];
    credentialsToClear: string[];
}

/**
 * Return which destination fields a save changes and which stored credentials
 * that save would clear (stored, and not re-entered in `changedValues`).
 * Returns `null` when the save clears nothing, so no confirmation is needed.
 */
export const getCredentialsClearedByDestinationChange = (
    resourceName: string,
    changedValues: { [key: string]: any },
    initialValues: { [key: string]: any },
    writeOnlyFieldsWithData: { [key: string]: boolean }
): CredentialClearing | null => {
    const binding = credentialBindingByResource[resourceName];
    if (!binding) {
        return null;
    }
    const changedFields = [
        ...binding.destinationFields.filter(
            (field) =>
                field in changedValues && norm(changedValues[field]) !== norm(initialValues[field])
        ),
        ...binding.tlsWeakeningFields.filter(
            (field) =>
                field in changedValues &&
                changedValues[field] === false &&
                Boolean(initialValues[field])
        ),
    ];
    if (changedFields.length === 0) {
        return null;
    }
    const credentialsToClear = binding.credentialFields.filter(
        (field) =>
            writeOnlyFieldsWithData[field] === true &&
            !(field in changedValues && norm(changedValues[field]) !== "")
    );
    if (credentialsToClear.length === 0) {
        return null;
    }
    return { changedFields, credentialsToClear };
};
